import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import CardHeader from '@mui/material/CardHeader';
import Typography from '@mui/material/Typography';
import CardContent from '@mui/material/CardContent';

import { paths } from 'src/routes/paths';
import { RouterLink } from 'src/routes/components';

import { useTranslate } from 'src/locales';
import { useGetCallListMembershipsQuery } from 'src/services/call-lists/call-lists.service';
import { CallListPersonType, CallListMembership } from 'src/services/call-lists/call-lists.types';

import Label from 'src/components/label';
import Iconify from 'src/components/iconify';

import { useCallListsEnabled } from '../hooks/use-call-lists-enabled';
import { formatDate, OUTCOME_ICON, OUTCOME_COLOR, formatDateTime, formatDuration } from '../utils';

// ----------------------------------------------------------------------

type Props = {
  personType: CallListPersonType;
  personId?: string;
};

/**
 * Call list history on a contact or candidate profile (JT1-1827): the viewer's own lists this person
 * is on, every attempt with its outcome, and who else in the agency has them on a list. Hidden when
 * there is nothing to show.
 */
export default function CallListHistoryCard({ personType, personId }: Props) {
  const { t } = useTranslate();
  const enabled = useCallListsEnabled();

  const { currentData } = useGetCallListMembershipsQuery(
    { entityType: personType, entityId: personId as string },
    { skip: !enabled || !personId }
  );

  const mine = currentData?.mine ?? [];
  const others = (currentData?.others ?? []).filter((shared) => !shared.is_mine);

  if (!mine.length && !others.length) return null;

  return (
    <Card data-testid="call-list-history-card">
      <CardHeader
        title={t('call_lists.profile.history_title')}
        avatar={<Iconify icon="solar:phone-calling-rounded-bold" />}
        sx={{ pb: 1 }}
      />
      <CardContent>
        <Stack spacing={2} divider={<Divider flexItem />}>
          {mine.map((membership) => <MembershipHistory key={membership.entry_id} membership={membership} />)}

          {others.length > 0 && (
            <Stack spacing={0.5}>
              {others.map((shared) => (
                <Typography key={shared.entry_id} variant="body2" color="text.secondary">
                  {t('call_lists.shared.also_on_theirs', { name: `${shared.owner.first_name} ${shared.owner.last_name}`.trim() })}
                  {` · ${t('call_lists.profile.added_on', { date: formatDate(shared.added_at) })}`}
                  {shared.last_attempt_at && ` · ${t('call_lists.shared.last_called', { when: formatDate(shared.last_attempt_at) })}`}
                </Typography>
              ))}
            </Stack>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}

function MembershipHistory({ membership }: { membership: CallListMembership }) {
  const { t } = useTranslate();

  return (
    <Stack spacing={1.25}>
      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
        <Typography variant="subtitle2">
          {membership.call_list_name}
          <Typography component="span" variant="caption" color="text.secondary">
            {` · ${t('call_lists.profile.added_on', { date: formatDate(membership.added_at) })}`}
          </Typography>
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <Label variant="soft" color={membership.status === 'active' ? 'info' : 'default'}>
            {membership.status === 'active'
              ? t('call_lists.attempt_of', { current: membership.attempt_number, max: membership.max_attempts })
              : t(`call_lists.statuses.${membership.status}`)}
          </Label>
          {/* Opens the call list on this person's card, not just the list. */}
          <Button
            size="small"
            component={RouterLink}
            href={paths.dashboard.tasks.callList({ listId: membership.call_list_id, entryId: membership.entry_id })}
            endIcon={<Iconify icon="eva:arrow-ios-forward-fill" width={16} />}
            data-testid="call-list-history-view"
          >
            {t('call_lists.profile.view_in_call_list')}
          </Button>
        </Stack>
      </Stack>

      {[...membership.attempts].reverse().map((attempt) => {
        const tone = OUTCOME_COLOR[attempt.outcome];
        return (
          <Stack key={attempt.id} direction="row" spacing={1.5} alignItems="flex-start">
            <Iconify
              icon={OUTCOME_ICON[attempt.outcome]}
              width={18}
              sx={{ mt: 0.25, color: tone === 'default' ? 'text.disabled' : `${tone}.main` }}
            />
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2">
                {t(`call_lists.outcomes.${attempt.outcome}`)}
                <Typography component="span" variant="caption" color="text.secondary">
                  {` · ${t('call_lists.attempt_of', { current: attempt.attempt_number, max: attempt.max_attempts })}`}
                  {attempt.duration_seconds ? ` · ${formatDuration(attempt.duration_seconds)}` : ''}
                </Typography>
              </Typography>
              <Typography variant="caption" color="text.secondary" component="div">
                {formatDateTime(attempt.created_at)}
                {attempt.account && ` · ${attempt.account.first_name} ${attempt.account.last_name}`.trimEnd()}
              </Typography>
              {attempt.notes && (
                <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>{attempt.notes}</Typography>
              )}
            </Box>
          </Stack>
        );
      })}

      <Typography variant="caption" color="text.secondary">
        {t(`call_lists.profile.added_from.${membership.added_from ?? 'unknown'}`)}
        {membership.added_by && ` · ${t('call_lists.added_by', { name: `${membership.added_by.first_name} ${membership.added_by.last_name}`.trim() })}`}
        {membership.reason && ` · ${t('call_lists.why')}: ${membership.reason}`}
      </Typography>
    </Stack>
  );
}
